/**
 * Check if Google Analytics is enabled based on cookie preferences
 */
const isAnalyticsEnabled = () => {
  const cookiePreferences = localStorage.getItem('cookiePreferences');
  if (!cookiePreferences) return false;

  try {
    const preferences = JSON.parse(cookiePreferences);
    return preferences?.analytics === true;
  } catch (e) {
    return false;
  }
};

/**
 * Track assessment completion
 */
export const trackAssessmentCompletion = (assessmentType, score, maturityLevel) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'assessment_complete', {
    event_category: 'Assessment',
    event_label: assessmentType,
    assessment_type: assessmentType,
    maturity_score: score,
    maturity_level: maturityLevel,
    page: window.location?.pathname
  });
};

/**
 * Track assessment start
 */
export const trackAssessmentStart = (assessmentType) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'assessment_start', {
    event_category: 'Assessment',
    event_label: assessmentType,
    assessment_type: assessmentType,
    page: window.location?.pathname
  });
};

/**
 * Track consultation booking
 */
export const trackConsultationBooking = (serviceType, bookingDate) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'consultation_booking', {
    event_category: 'Conversion',
    event_label: serviceType,
    service_type: serviceType,
    booking_date: bookingDate,
    page: window.location?.pathname
  });
};

/**
 * Track service page engagement
 */
export const trackServiceEngagement = (serviceName, engagementType, duration = null) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'service_engagement', {
    event_category: 'Engagement',
    event_label: serviceName,
    service_name: serviceName,
    engagement_type: engagementType,
    engagement_duration: duration,
    page: window.location?.pathname
  });
};

/**
 * Track newsletter subscription
 */
export const trackNewsletterSubscription = (interests) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'newsletter_subscription', {
    event_category: 'Lead Generation',
    event_label: 'Newsletter',
    interests: interests?.join(', '),
    page: window.location?.pathname
  });
};

/**
 * Track form submission
 */
export const trackFormSubmission = (formName, formType) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'form_submit', {
    event_category: 'Form',
    event_label: formName,
    form_name: formName,
    form_type: formType,
    page: window.location?.pathname
  });
};

/**
 * Track CTA button clicks
 */
export const trackCTAClick = (ctaName, ctaLocation) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'cta_click', {
    event_category: 'CTA',
    event_label: ctaName,
    cta_name: ctaName,
    cta_location: ctaLocation,
    page: window.location?.pathname
  });
};

/**
 * Track PDF downloads
 */
export const trackPDFDownload = (reportType, reportName) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'pdf_download', {
    event_category: 'Download',
    event_label: reportName,
    report_type: reportType,
    report_name: reportName,
    page: window.location?.pathname
  });
};

/**
 * Track calculator usage
 */
export const trackCalculatorUsage = (calculatorType, result) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'calculator_usage', {
    event_category: 'Tool',
    event_label: calculatorType,
    calculator_type: calculatorType,
    result_value: result,
    page: window.location?.pathname
  });
};

/**
 * Track video engagement
 */
export const trackVideoEngagement = (videoTitle, action, progress = null) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'video_engagement', {
    event_category: 'Video',
    event_label: videoTitle,
    video_title: videoTitle,
    video_action: action, // 'play', 'pause', 'complete'
    video_progress: progress,
    page: window.location?.pathname
  });
};

/**
 * Track campaign performance
 */
export const trackCampaignClick = (campaignName, campaignSource, campaignMedium) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'campaign_click', {
    event_category: 'Campaign',
    event_label: campaignName,
    campaign_name: campaignName,
    campaign_source: campaignSource,
    campaign_medium: campaignMedium,
    page: window.location?.pathname
  });
};

// ============ CONVERSION TRACKING EVENTS ============

/**
 * Track booking form start (user begins filling out booking form)
 */
export const trackBookingStart = (serviceType, source = 'direct') => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'booking_start', {
    event_category: 'Conversion',
    event_label: 'Booking Form Started',
    service_type: serviceType,
    booking_source: source, // 'assessment', 'direct', 'cta'
    page: window.location?.pathname
  });
};

/**
 * Track booking confirmation (successful booking completion)
 */
export const trackBookingConfirmation = (serviceType, bookingId, source = 'direct') => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'booking_confirmation', {
    event_category: 'Conversion',
    event_label: 'Booking Confirmed',
    service_type: serviceType,
    booking_id: bookingId,
    booking_source: source,
    page: window.location?.pathname,
    value: 1 // Conversion value
  });
};

/**
 * Track assessment-to-booking conversion
 */
export const trackAssessmentToBooking = (assessmentType, assessmentScore, maturityLevel, serviceType, timeToBookingMinutes) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'assessment_to_booking_conversion', {
    event_category: 'Conversion',
    event_label: 'Assessment to Booking',
    assessment_type: assessmentType,
    assessment_score: assessmentScore,
    maturity_level: maturityLevel,
    service_type: serviceType,
    time_to_booking_minutes: timeToBookingMinutes,
    page: window.location?.pathname,
    value: 1
  });
};

/**
 * Track lead quality score
 */
export const trackLeadQuality = (leadQualityScore, assessmentType, serviceType) => {
  if (!isAnalyticsEnabled() || typeof window.gtag === 'undefined') return;

  window.gtag('event', 'lead_quality_scored', {
    event_category: 'Lead Generation',
    event_label: 'Lead Quality Score',
    lead_quality_score: leadQualityScore,
    assessment_type: assessmentType,
    service_type: serviceType,
    page: window.location?.pathname
  });
};

export default {
  trackAssessmentCompletion,
  trackAssessmentStart,
  trackConsultationBooking,
  trackServiceEngagement,
  trackNewsletterSubscription,
  trackFormSubmission,
  trackCTAClick,
  trackPDFDownload,
  trackCalculatorUsage,
  trackVideoEngagement,
  trackCampaignClick,
  trackBookingStart,
  trackBookingConfirmation,
  trackAssessmentToBooking,
  trackLeadQuality
};